import React, { useEffect, useRef, useCallback } from 'react';
import "../../assets/style/Skills.css"
import SkillsSwiper from './SkillsSwiper';
import Fonction from '../Fonction/Fonction';

const TAGS = [
    { label: 'React', color: '#61dafb' },
    { label: 'TypeScript', color: '#3178c6' },
    { label: 'CSS3', color: '#264de4' },
    { label: 'HTML5', color: '#e34f26' },
    { label: 'Flexbox', color: '#a78bfa' },
    { label: 'Mobile', color: '#fe1a4e' },
    { label: 'Git', color: '#f34f29' },
    { label: 'Python', color: '#ff2d20' },
    { label: 'Node.js', color: '#68a063' },
    { label: 'Figma', color: '#f24e1e' },
    { label: 'SQL', color: '#38bdf8' },
    { label: 'Tailwind', color: '#06b6d4' },
    { label: 'Bootstrap', color: '#7952b3' },
    { label: 'PostgreSQL', color: '#336791' },
    { label: 'UX/UI', color: '#ff79c6' },
    { label: 'Responsive', color: '#50fa7b' },
    { label: 'Next.js', color: '#ffffff' },
    { label: 'Symfony', color: '#aaaaff' },
];

const Skills: React.FC = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const rafRef = useRef<number>(0);

    const drawGrid = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        canvas.width = canvas.offsetWidth;
        canvas.height = canvas.offsetHeight;

        const spacing = 40;
        const cols = Math.ceil(canvas.width / spacing) + 1;
        const rows = Math.ceil(canvas.height / spacing) + 1;
        const now = Date.now() / 1200;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.strokeStyle = 'rgba(254,26,78,0.1)';
        ctx.lineWidth = 0.7;

        // Horizontal wavy lines
        for (let r = 0; r < rows; r++) {
            ctx.beginPath();
            for (let c = 0; c < cols; c++) {
                const x = c * spacing;
                const wave = Math.sin(c * 0.35 + now + r * 0.25) * 7;
                const y = r * spacing + wave;
                c === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
            }
            ctx.stroke();
        }

        // Vertical wavy lines
        for (let c = 0; c < cols; c++) {
            ctx.beginPath();
            for (let r = 0; r < rows; r++) {
                const y = r * spacing;
                const wave = Math.sin(r * 0.35 + now + c * 0.25) * 7;
                const x = c * spacing + wave;
                r === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
            }
            ctx.stroke();
        }

        // Intersection dots
        ctx.fillStyle = 'rgba(254,26,78,0.2)';
        for (let c = 0; c < cols; c++) {
            for (let r = 0; r < rows; r++) {
                const wX = Math.sin(r * 0.35 + now + c * 0.25) * 7;
                const wY = Math.sin(c * 0.35 + now + r * 0.25) * 7;
                ctx.beginPath();
                ctx.arc(c * spacing + wX, r * spacing + wY, 1.5, 0, Math.PI * 2);
                ctx.fill();
            }
        }

        rafRef.current = requestAnimationFrame(drawGrid);
    }, []);

    useEffect(() => {
        rafRef.current = requestAnimationFrame(drawGrid);
        const onResize = () => {
            if (canvasRef.current) {
                canvasRef.current.width = canvasRef.current.offsetWidth;
                canvasRef.current.height = canvasRef.current.offsetHeight;
            }
        };
        window.addEventListener('resize', onResize);
        return () => {
            cancelAnimationFrame(rafRef.current);
            window.removeEventListener('resize', onResize);
        };
    }, [drawGrid]);

    return (
        <div>
            <section className="skills section" id="skills">
                {/* Animated grid canvas */}
                <canvas className="skills__canvas" ref={canvasRef} aria-hidden="true" />

                {/* Floating tech tags */}
                <div className="skills__tags" aria-hidden="true">
                    {TAGS.map((tag, i) => (
                        <span
                            key={tag.label}
                            className="skills__tag"
                            style={{
                                '--tag-color': tag.color,
                                '--tag-delay': `${(i * 0.65) % 7}s`,
                                '--tag-duration': `${7 + (i * 1.1) % 6}s`,
                                '--tag-x': `${(i * 37 + 5) % 88}%`,
                                '--tag-y': `${(i * 23 + 8) % 82}%`,
                            } as React.CSSProperties}
                        >
                            {tag.label}
                        </span>
                    ))}
                </div>

                <div className="contenu" style={{ position: 'relative', zIndex: 1 }}>
                    <h2 data-heading="Mon niveau technique" className="section__title">
                        Compétences
                    </h2>
                    <div className="skills__container container">
                        <SkillsSwiper />
                    </div>
                    <Fonction />
                </div>
            </section>
        </div>
    );
};

export default Skills;
